import { useMutation } from '@apollo/client';
import { Button, Form, Label, type IFormRender } from '@busybusy/webapp-react-ui';
import { UPDATE_EQUIPMENT } from 'apollo/mutations/equipment-mutations';
import { EmptyState } from 'components';
import EquipmentInspectionChecklistPickerFormField from 'components/domain/equipment/EquipmentInspectionChecklistPickerFormField/EquipmentInspectionChecklistPickerFormField';
import type { IManageEquipmentRowInfo } from 'containers/manage-euipment/ManageEquipment';
import { ManageEquipmentGroupActionInspectionsModes } from 'containers/manage-euipment/ManageEquipmentGroupActionInspections/ManageEquipmentGroupActionInspections';
import { useLoaderToastPromise } from 'hooks/promise';
import { map, uniq } from 'lodash';
import { useMemo } from 'react';
import { t } from 'utils/localize';

interface ManageEquipmentBulkUpdateOperationInspectionsForm {
  checkListId: string | undefined;
}
interface IManageEquipmentBulkUpdateOperationInspectionsProps {
  mode: 'updatePreOperationInspections' | 'updatePostOperationInspections';
  checkedRows: IManageEquipmentRowInfo[];
  onSubmit: () => void;
}
export function ManageEquipmentBulkUpdateOperationInspections(
  props: IManageEquipmentBulkUpdateOperationInspectionsProps
) {
  const { mode, checkedRows, onSubmit } = props;
  const modeInfo = ManageEquipmentGroupActionInspectionsModes[mode];
  const [editEquipmentMutation] = useMutation(UPDATE_EQUIPMENT);

  const { formData, placeholderText } = useMemo((): {
    formData: ManageEquipmentBulkUpdateOperationInspectionsForm | null;
    placeholderText: string | undefined;
  } => {
    const selections = uniq(map(checkedRows, (row) => row.equipment[modeInfo.field] ?? null));
    const hasIdenticalSelections = selections.length === 1; // includes the case where all equipment have no checklist assigned (all null)
    const hasMultipleSelections = selections.length > 1;

    if (hasIdenticalSelections) {
      return {
        formData: { checkListId: selections[0] ?? undefined },
        placeholderText: undefined,
      };
    } else if (hasMultipleSelections) {
      return {
        formData: { checkListId: undefined },
        placeholderText: t('Multiple Selected'),
      };
    }

    return {
      formData: null,
      placeholderText: undefined,
    };
  }, [checkedRows, modeInfo.field]);

  const submitBulkUpdate = useLoaderToastPromise(
    (proposedFormData: ManageEquipmentBulkUpdateOperationInspectionsForm) => {
      const mutations = checkedRows.map(({ equipment }) => ({
        variables: {
          equipment: {
            id: equipment.id,
            [modeInfo.field]: proposedFormData.checkListId ?? null,
          },
        },
      }));

      return Promise.all(mutations.map(editEquipmentMutation)).then(() => {
        onSubmit();
      });
    }
  );

  // impossible states, but good to account for them
  if (!checkedRows.length || !formData) {
    return <EmptyState title={t('No Equipment Selected')} />;
  }

  return (
    <Form
      data={formData}
      onSubmit={submitBulkUpdate}
      allowMultipleSubmissions
      render={(form: IFormRender<ManageEquipmentBulkUpdateOperationInspectionsForm>) => (
        <>
          <Label>{t(modeInfo.fieldLabel)}</Label>
          <EquipmentInspectionChecklistPickerFormField
            name="checkListId"
            form={form}
            placeholder={placeholderText}
            clearable={true}
          />
          <Button className="pl-5 pr-5" type="primary" onClick={form.handleSubmit}>
            {t('Save')}
          </Button>
        </>
      )}
    />
  );
}
